import { createIdempotencyKey } from "../api/client";

interface StableOperationRecord {
  signature: string;
  key: string;
}

export interface ImportCheckpoint {
  importId: string;
  signature: string;
  completedRows: string[];
  rowKeys: Record<string, string>;
}

function hashText(value: string): string {
  let first = 0x811c9dc5;
  let second = 0x9e3779b9;
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    first ^= code;
    first = Math.imul(first, 0x01000193);
    second ^= code + index;
    second = Math.imul(second, 0x85ebca6b);
  }
  return `${(first >>> 0).toString(16).padStart(8, "0")}${(second >>> 0).toString(16).padStart(8, "0")}`;
}

function operationStorageKey(tenantId: string, operation: string): string {
  return `leylion.first-login.operation:${tenantId}:${operation}`;
}

function importStorageKey(tenantId: string): string {
  return `leylion.first-login.import:${tenantId}`;
}

function parseRecord<T>(value: string | null): T | null {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

export function getStableOperationKey(
  tenantId: string,
  operation: string,
  canonicalPayload: string
): string {
  const signature = hashText(canonicalPayload);
  const storageKey = operationStorageKey(tenantId, operation);
  const existing = parseRecord<StableOperationRecord>(localStorage.getItem(storageKey));
  if (existing?.signature === signature && existing.key) {
    return existing.key;
  }

  const record: StableOperationRecord = {
    signature,
    key: createIdempotencyKey()
  };
  localStorage.setItem(storageKey, JSON.stringify(record));
  return record.key;
}

export function clearStableOperationKey(
  tenantId: string,
  operation: string
): void {
  localStorage.removeItem(operationStorageKey(tenantId, operation));
}

export function getOrCreateImportCheckpoint(
  tenantId: string,
  canonicalImport: string
): ImportCheckpoint {
  const signature = hashText(canonicalImport);
  const storageKey = importStorageKey(tenantId);
  const existing = parseRecord<ImportCheckpoint>(localStorage.getItem(storageKey));
  if (existing?.signature === signature && existing.importId) {
    return {
      importId: existing.importId,
      signature,
      completedRows: Array.isArray(existing.completedRows) ? existing.completedRows : [],
      rowKeys: existing.rowKeys ?? {}
    };
  }

  const checkpoint: ImportCheckpoint = {
    importId: createIdempotencyKey(),
    signature,
    completedRows: [],
    rowKeys: {}
  };
  localStorage.setItem(storageKey, JSON.stringify(checkpoint));
  return checkpoint;
}

export function importRowToken(
  checkpoint: ImportCheckpoint,
  rowIndex: number,
  canonicalRow: string
): string {
  return `${rowIndex}:${hashText(canonicalRow)}`;
}

export function getImportRowKey(
  tenantId: string,
  checkpoint: ImportCheckpoint,
  rowToken: string
): string {
  const existing = checkpoint.rowKeys[rowToken];
  if (existing) return existing;

  checkpoint.rowKeys[rowToken] = createIdempotencyKey();
  persistImportCheckpoint(tenantId, checkpoint);
  return checkpoint.rowKeys[rowToken];
}

export function markImportRowCompleted(
  tenantId: string,
  checkpoint: ImportCheckpoint,
  rowToken: string
): void {
  if (!checkpoint.completedRows.includes(rowToken)) {
    checkpoint.completedRows.push(rowToken);
  }
  persistImportCheckpoint(tenantId, checkpoint);
}

export function isImportRowCompleted(
  checkpoint: ImportCheckpoint,
  rowToken: string
): boolean {
  return checkpoint.completedRows.includes(rowToken);
}

export function clearImportCheckpoint(tenantId: string): void {
  localStorage.removeItem(importStorageKey(tenantId));
}

function persistImportCheckpoint(
  tenantId: string,
  checkpoint: ImportCheckpoint
): void {
  localStorage.setItem(importStorageKey(tenantId), JSON.stringify(checkpoint));
}
